import React from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { Body5, Body7, Container, H5 } from '@ui';
import { TextField } from '@ui/inputs';
import { FlatButton, SkeletonButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { createSpec } from '@services/scoreSpecs';
import { Consequence, PassiveCard } from '@features/scoreSpec/styles';
import { writeTarget } from '@features/scoreSpec/environment';

// The whole spec, pasted.
//
// The other two modes exist because authoring a spec by hand is hard: /advanced-score-spec walks the
// four questions and validates every cross-field rule it can, and /edit-score-spec hides all of it
// behind three fields. This one does neither. The text IS the request body, sent byte-for-byte, and
// the only things checked here are that it exists and that it parses.
//
// That is deliberate, and it means one thing worth knowing: every spec currently in the store is
// written in a shape create rejects (`enabled_in`, `score_jsonpath`, and three of them declare both
// output contracts), so pasting an existing spec back in returns a 422. Nothing here warns about that
// or edits it away — the server's message is shown verbatim instead, which is the honest answer for a
// form whose contract with the operator is "what you typed is what I sent".

const EnvBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ theme, $production }) => ($production ? theme.themeColors.warning : theme.themeColors.lowContrastBorder)};
`;

const PLACEHOLDER = `{
  "score_spec_id": "my_score_v1",
  "version": 1,
  "description": "…",
  "trigger": { … },
  "document_set": { … },
  "score_gen": { … },
  "output_extraction": { … }
}`;

export const PasteSpecForm = () => {
  const navigate = useNavigate();
  const target = writeTarget();

  const [text, setText] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [created, setCreated] = React.useState(null);

  // Parsed on every keystroke rather than on submit: the operator finds out where the brace is
  // missing while they are still looking at it.
  const trimmed = text.trim();
  const isEmpty = trimmed === '';
  let parsed = null;
  let parseError = null;
  if (!isEmpty) {
    try {
      parsed = JSON.parse(trimmed);
    } catch (e) {
      parseError = e.message;
    }
  }

  const canSubmit = !isEmpty && !parseError && !isSubmitting;

  // `disabled` on the shared buttons is styling only — the click still fires — so the guard lives here.
  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);
    const res = await createSpec(parsed);
    setIsSubmitting(false);
    if (res.status === 200 && res.spec) {
      setCreated(res.spec);
      return;
    }
    // Verbatim, including the 409 for an id that already exists. Bumping the version here would mean
    // editing the payload, which is the one thing this form promises not to do.
    setError(res.error);
  };

  if (created) {
    return (
      <PassiveCard aria-label="Draft created">
        <H5>{`Draft created — ${created.score_spec_id}`}</H5>
        <Container top={8}>
          <Body5>
            It is not running yet, and nothing beyond the shape of the request was checked — a spec is compiled at lock,
            not at create.
          </Body5>
        </Container>
        <Container top={24} row verticallyCenter>
          <FlatButton onClick={() => navigate(`${ROUTES.scoreSpecSpecs}/${created.score_spec_id}`)}>
            Open the draft
          </FlatButton>
          <Container left={16}>
            <SkeletonButton
              onClick={() => {
                setCreated(null);
                setText('');
              }}
            >
              Paste another
            </SkeletonButton>
          </Container>
        </Container>
      </PassiveCard>
    );
  }

  return (
    <div>
      <EnvBar $production={target.isProduction}>
        <Body7 bold>{`Writing to ${target.label}`}</Body7>
        <Body7 isLowContrast>{target.host}</Body7>
        <Body7 isLowContrast>· Dev/QA and production are separate stores; a spec created here exists only here.</Body7>
      </EnvBar>

      <Container top={24}>
        <TextField
          id="paste-spec-json"
          label="The spec, as JSON"
          aria-label="The spec, as JSON"
          multiline
          mono
          rows={26}
          value={text}
          onChange={setText}
          placeholder={PLACEHOLDER}
          invalid={!!parseError}
          hint={parseError ? `Not valid JSON — ${parseError}` : 'Sent exactly as typed. Nothing here rewrites it.'}
        />
      </Container>

      {error && (
        <Container top={16}>
          <Consequence $tone="bad">{error}</Consequence>
        </Container>
      )}

      <Container top={24} row verticallyCenter>
        <FlatButton onClick={handleSubmit} disabled={!canSubmit}>
          {isSubmitting ? 'Creating…' : 'Create draft'}
        </FlatButton>
        <Container left={16}>
          <Body7 isLowContrast>
            {isEmpty
              ? 'Paste a spec definition to create it.'
              : parseError
              ? 'Fix the JSON and this becomes available.'
              : 'Creates a draft. Drafts run nothing and can be deleted outright.'}
          </Body7>
        </Container>
      </Container>
    </div>
  );
};
